
import { supabase } from "@/lib/supabase";
import { DocumentSection as DocumentSectionType, TopicRelationship as TopicRelationshipType } from "@/types/current-affairs";

// Using local interfaces for database operations that match the snake_case in the database
interface DbDocumentSection {
  id: string;
  document_id: string;
  section_title: string;
  section_order: number;
  content: string;
  metadata?: Record<string, any>;
}

interface DbTopicRelationship {
  document_id: string;
  primary_topic: string;
  related_topic: string;
  relationship_type: 'subtopic' | 'related' | 'prerequisite';
  relationship_strength: number;
}

export async function extractDocumentSections(documentId: string, content: string): Promise<DocumentSectionType[]> {
  // Basic implementation - split content into sections
  const sections: Omit<DbDocumentSection, 'id'>[] = content.split('\n\n').map((sectionContent, index) => ({
    document_id: documentId,
    section_title: `Section ${index + 1}`,
    section_order: index,
    content: sectionContent,
    metadata: {}
  }));

  // Store sections in database
  const { data, error } = await supabase
    .from('document_sections')
    .insert(sections)
    .select();

  if (error) {
    console.error('Error storing document sections:', error);
    throw error;
  }

  // Convert from DB snake_case to camelCase for the frontend
  return (data || []).map(section => ({
    id: section.id,
    documentId: section.document_id,
    sectionTitle: section.section_title,
    sectionOrder: section.section_order,
    content: section.content,
    metadata: section.metadata
  }));
}

export async function analyzeTopicRelationships(documentId: string, sections: DocumentSectionType[]): Promise<TopicRelationshipType[]> {
  // Basic topic relationship extraction
  const dbRelationships: DbTopicRelationship[] = sections.flatMap((section, index) => {
    if (index < sections.length - 1) {
      return [{
        document_id: documentId,
        primary_topic: section.sectionTitle,
        related_topic: sections[index + 1].sectionTitle,
        relationship_type: 'related',
        relationship_strength: 0.5
      }];
    }
    return [];
  });

  // Insert into the database using snake_case
  const { data, error } = await supabase
    .from('topic_relationships')
    .insert(dbRelationships)
    .select();

  if (error) {
    console.error('Error storing topic relationships:', error);
    throw error;
  }

  // Convert from DB snake_case to camelCase for the frontend
  return (data || []).map(relation => ({
    id: relation.id,
    documentId: relation.document_id,
    primaryTopic: relation.primary_topic,
    relatedTopic: relation.related_topic,
    relationshipType: relation.relationship_type,
    relationshipStrength: relation.relationship_strength
  }));
}

// Progressive Difficulty Adjustment
export async function trackUserTopicMastery(userId: string, topic: string, correct: boolean, timeSpentMs: number) {
  // Get existing topic mastery data
  const { data: existingMastery, error: fetchError } = await supabase
    .from('concept_mastery_maps')
    .select('*')
    .eq('user_id', userId)
    .eq('concept_name', topic)
    .single();

  if (fetchError && fetchError.code !== 'PGRST116') { // PGRST116 is "no rows returned" which is fine
    console.error('Error fetching topic mastery data:', fetchError);
    return null;
  }

  // Calculate new mastery level
  const attempts = (existingMastery?.attempts || 0) + 1;
  const correctAttempts = (existingMastery?.correct_attempts || 0) + (correct ? 1 : 0);
  const avgTimeSeconds = existingMastery 
    ? ((existingMastery.avg_time_seconds * (attempts - 1)) + (timeSpentMs / 1000)) / attempts
    : timeSpentMs / 1000;
  
  // Mastery level 0-100
  const accuracyScore = (correctAttempts / attempts) * 100;
  const speedFactor = Math.min(1, 30 / avgTimeSeconds); // Higher for faster answers (cap at 30s)
  const masteryLevel = Math.round(accuracyScore * 0.7 + (speedFactor * 30));

  // Store or update mastery data
  const masteryData = {
    user_id: userId,
    concept_name: topic,
    subject: topic.split(' ')[0], // Simplified subject extraction
    attempts,
    correct_attempts: correctAttempts,
    avg_time_seconds: Math.round(avgTimeSeconds),
    mastery_level: masteryLevel,
    last_tested_at: new Date().toISOString()
  };

  if (existingMastery) {
    const { error: updateError } = await supabase
      .from('concept_mastery_maps')
      .update(masteryData)
      .eq('id', existingMastery.id);
    
    if (updateError) {
      console.error('Error updating mastery data:', updateError);
      return null;
    }
  } else {
    const { error: insertError } = await supabase
      .from('concept_mastery_maps')
      .insert(masteryData);
    
    if (insertError) {
      console.error('Error inserting mastery data:', insertError);
      return null;
    }
  }

  return masteryLevel;
}

// Get recommended content difficulty for a user on a topic
export async function getRecommendedDifficulty(userId: string, topic: string): Promise<'easy' | 'medium' | 'hard'> {
  const { data, error } = await supabase
    .from('concept_mastery_maps')
    .select('mastery_level')
    .eq('user_id', userId)
    .eq('concept_name', topic)
    .single();

  if (error || !data) {
    console.log('No mastery data found, defaulting to easy difficulty');
    return 'easy';
  }

  // Adjust difficulty based on mastery level
  if (data.mastery_level >= 80) {
    return 'hard';
  } else if (data.mastery_level >= 50) {
    return 'medium';
  } else {
    return 'easy';
  }
}

// Content Relationship Mapping
export async function getRelatedContent(topic: string, limit: number = 5): Promise<{documentId: string, sectionTitle: string, relationshipType: string, strength: number}[]> {
  // Find related topics from the relationship graph
  const { data: relationData, error: relationError } = await supabase
    .from('topic_relationships')
    .select('document_id, primary_topic, related_topic, relationship_type, relationship_strength')
    .or(`primary_topic.ilike.%${topic}%,related_topic.ilike.%${topic}%`)
    .order('relationship_strength', { ascending: false })
    .limit(limit);

  if (relationError) {
    console.error('Error fetching related topics:', relationError);
    return [];
  }

  // Transform the data for frontend use
  return (relationData || []).map(relation => {
    // Determine if the topic is the primary or related topic
    const isTopicPrimary = relation.primary_topic.toLowerCase().includes(topic.toLowerCase());
    
    return {
      documentId: relation.document_id,
      // If our topic is the primary one, then show the related topic as the section title, otherwise show primary
      sectionTitle: isTopicPrimary ? relation.related_topic : relation.primary_topic,
      relationshipType: relation.relationship_type,
      strength: relation.relationship_strength
    };
  });
}

// Build a knowledge graph from content
export async function buildKnowledgeGraph(documentId: string, sections: DocumentSectionType[]): Promise<void> {
  // Create more sophisticated relationships between sections
  const relationships: DbTopicRelationship[] = [];
  
  // Analysis uses a simple keyword matching approach for demonstration
  // In a real application, this would use NLP or other more advanced techniques
  for (let i = 0; i < sections.length; i++) {
    const sectionA = sections[i];
    
    // Compare with all other sections to find relationships
    for (let j = 0; j < sections.length; j++) {
      if (i === j) continue; // Skip self
      
      const sectionB = sections[j];
      
      // Check content overlap (simplified) - count shared words over 4 characters
      const contentA = sectionA.content.toLowerCase();
      const contentB = sectionB.content.toLowerCase();
      
      const wordsA = contentA.split(/\s+/).filter(word => word.length > 4);
      const wordsB = contentB.split(/\s+/).filter(word => word.length > 4);
      
      // Count shared significant words as a measure of relationship
      const sharedWords = wordsA.filter(word => wordsB.includes(word));
      
      if (sharedWords.length > 0) {
        // Calculate relationship strength based on overlap
        const relationshipStrength = sharedWords.length / Math.max(wordsA.length, wordsB.length);
        
        // Only include relationships with meaningful strength
        if (relationshipStrength > 0.1) {
          relationships.push({
            document_id: documentId,
            primary_topic: sectionA.sectionTitle,
            related_topic: sectionB.sectionTitle,
            relationship_type: i < j ? 'prerequisite' : 'related', // If earlier section, mark as prerequisite
            relationship_strength: relationshipStrength
          });
        }
      }
    }
  }

  // Store the relationships
  if (relationships.length > 0) {
    const { error } = await supabase
      .from('topic_relationships')
      .insert(relationships);

    if (error) {
      console.error('Error storing knowledge graph relationships:', error);
    }
  }
}
